/**
 * Joliet overlay (UCS-2BE / UTF-16BE long Unicode names).
 *
 * Joliet stores a parallel directory tree under the SVD (descriptor type 2).
 * FILES in the SVD share their LBA + size with the PVD tree, so they can be
 * matched by extent. DIRECTORIES cannot: each tree has its own directory
 * extents, at different LBAs. The original implementation keyed everything by
 * LBA, so folder names were NEVER restored — measured 2026-07-30 on an image
 * with a folder called «Мои документы»: its files came back correctly named
 * while the folder itself stayed `FILE`, and a second non-Latin folder would
 * have collided into `FILE~1`. On extraction that merges two folders' contents
 * into one, which is data loss shaped like a naming quirk.
 *
 * Directories are therefore matched by CONTENT SIGNATURE: the sorted set of all
 * file extents in the subtree. That is writer-independent (mkisofs sorts the two
 * trees by their own identifiers, so positional pairing is unsafe) and
 * order-independent. A signature shared by two directories is ambiguous and is
 * skipped rather than guessed; an empty directory has no signature and keeps its
 * ISO9660 name.
 *
 * Surface: window.__DiskImage.applyJoliet(iso) → Promise<Iso9660>
 *   Adds .unicodeName / .joliet flag to nodes.
 *
 * Escape sequences distinguish UCS-2 levels (1..3); decoder is the same
 * because TextDecoder('utf-16be') tolerates surrogate variations.
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.applyJoliet) return;

    var SECTOR = 2048;

    function _u32leAt(b, off) {
        return ((b[off] | (b[off + 1] << 8) | (b[off + 2] << 16) | (b[off + 3] << 24)) >>> 0);
    }

    function _decodeJolietName(b, off, lenFi) {
        if (lenFi === 1 && (b[off] === 0x00 || b[off] === 0x01)) return null;
        // UCS-2BE / UTF-16BE
        var bytes = b.subarray(off, off + lenFi);
        var name = new TextDecoder('utf-16be', { fatal: false }).decode(bytes);
        // Strip ;1 version suffix
        var semi = name.lastIndexOf(';');
        if (semi >= 0) name = name.slice(0, semi);
        if (name.endsWith('.')) name = name.slice(0, -1);
        return name;
    }

    /**
     * Read ONE directory extent into a flat record list. Records that a reader
     * must skip ('.', '..', zero padding to the next sector) are dropped here so
     * the callers stay simple.
     */
    function _readDirRecords(reader, lba, size) {
        return reader.bytes(lba * SECTOR, size).then(function (b) {
            var out = [];
            var off = 0, skipped = 0, open = null;
            while (off < b.length) {
                var lenDr = b[off];
                if (lenDr === 0) { off = Math.ceil((off + 1) / SECTOR) * SECTOR; continue; }
                if (lenDr < 33) break;
                if (skipped < 2) { skipped++; off += lenDr; continue; }
                var lenFi = b[off + 32];
                var rec = {
                    lba: _u32leAt(b, off + 2),
                    size: _u32leAt(b, off + 10),
                    isDir: !!(b[off + 25] & 0x02),
                    name: _decodeJolietName(b, off + 33, lenFi),
                };
                var multi = !!(b[off + 25] & 0x80);
                off += lenDr;
                // A later section of a multi-extent file (> 4 GiB) is the same
                // file: the ISO 9660 reader merges it into one node keyed by the
                // FIRST extent, so here it must not count as a second file — a
                // directory's signature would never match again.
                if (open && rec.name === open.name) { open.size += rec.size; if (!multi) open = null; continue; }
                open = (multi && !rec.isDir) ? rec : null;
                out.push(rec);
            }
            return out;
        });
    }

    /** Recursively mirror the SVD tree: { name, isDir, lba, size, children }. */
    function _readSvdTree(reader, lba, size, depth) {
        if (depth > 32) return Promise.resolve([]);
        return _readDirRecords(reader, lba, size).then(function (recs) {
            var chain = Promise.resolve();
            recs.forEach(function (r) {
                if (!r.isDir || !r.size) { r.children = []; return; }
                chain = chain.then(function () {
                    return _readSvdTree(reader, r.lba, r.size, depth + 1).then(function (kids) {
                        r.children = kids;
                    });
                });
            });
            return chain.then(function () { return recs; });
        });
    }

    /** Sorted set of every FILE extent below this node — its content identity. */
    function _signature(children) {
        var lbas = [];
        (function walk(list) {
            (list || []).forEach(function (n) {
                if (n.isDir) walk(n.children);
                else if (n.size > 0) lbas.push(n.lba);
            });
        })(children);
        if (!lbas.length) return null;
        lbas.sort(function (a, b) { return a - b; });
        return lbas.join(',');
    }

    /** Index dirs by signature; anything ambiguous is recorded as unusable. */
    function _indexDirs(children, map, dupes) {
        (children || []).forEach(function (n) {
            if (!n.isDir) return;
            var sig = _signature(n.children);
            if (sig) {
                if (map.has(sig)) dupes.add(sig);
                else map.set(sig, n);
            }
            _indexDirs(n.children, map, dupes);
        });
    }

    function applyJoliet(iso) {
        if (!iso || !iso.svdParsed) return Promise.resolve(iso);
        var reader = iso.reader;
        var svdParsed = iso.svdParsed;
        return _readSvdTree(reader, svdParsed.rootLba, svdParsed.rootSize, 0).then(function (svdRoot) {
            // Files: exact match on the extent they share with the PVD tree. An
            // extent more than one Joliet file points at (writers commonly give
            // every empty file the same location) names nothing — it is skipped
            // rather than guessed, the same rule as for directories below.
            var byLba = new Map(), sharedLba = new Set();
            (function walk(list) {
                (list || []).forEach(function (n) {
                    if (n.isDir) walk(n.children);
                    else if (n.name) { if (byLba.has(n.lba)) sharedLba.add(n.lba); else byLba.set(n.lba, n.name); }
                });
            })(svdRoot);
            sharedLba.forEach(function (l) { byLba.delete(l); });

            // Directories: match on content signature (see the header note).
            var dirsBySig = new Map(), ambiguous = new Set();
            _indexDirs(svdRoot, dirsBySig, ambiguous);

            var files = 0, dirs = 0, skipped = 0;
            iso.iterate().forEach(function (node) {
                if (node.isDir) {
                    if (node === iso.root) return;      // the root has no name
                    var sig = _signature(node.children);
                    if (!sig) { skipped++; return; }     // empty dir — nothing to match on
                    if (ambiguous.has(sig)) { skipped++; return; }
                    var hit = dirsBySig.get(sig);
                    if (hit && hit.name) {
                        node.unicodeName = hit.name;
                        node.joliet = true;
                        dirs++;
                    } else {
                        skipped++;
                    }
                    return;
                }
                var nm = byLba.get(node.lba);
                if (nm) { node.unicodeName = nm; node.joliet = true; files++; }
            });

            iso.jolietApplied = true;
            iso.jolietCount = files + dirs;
            iso.jolietStats = { files: files, dirs: dirs, unmatched: skipped };
            return iso;
        }).catch(function () {
            // Best-effort overlay; if the SVD walk fails the PVD tree still works
            return iso;
        });
    }

    w.__DiskImage.applyJoliet = applyJoliet;
})(window);
